import { FC, useCallback, useContext, useMemo } from 'react'

import { Text } from '@chakra-ui/react'
import { MODAL_TYPES } from '@editor/config/constants'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { EditorPanelItem } from '@editor/interfaces/editor'
import { MaskRegion } from '@editor/interfaces/maskRegion'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { setElementDeleting } from '@editor/pages/projects/editor/store/editor'
import {
  removeMaskRegion,
  resetWorking,
  setMaskRegions,
  updateMaskRegion,
} from '@editor/pages/projects/editor/tools/MaskPCD/store'
import { deleteMaskingRegion } from '@editor/services/MaskingRegion'
import { withPromiseContained } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setSelectedElementIds } from '@editor/store/tool'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'

import { Layer } from '../components/Layer'

const MaskingRegions: FC = () => {
  // Store
  const dispatch = useAppDispatch()
  const maskRegions = useSelector((state: RootState) => state.maskPCD.regions)
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const permissionSet = useSelector((state: RootState) => state.editor.permissionSet)
  const userType = useSelector((state: RootState) => state.user.userType)
  const workingMaskPoints = useSelector((state: RootState) => state.maskPCD.workingMaskCuboid)
  const savingMask = useSelector((state: RootState) => state.maskPCD.savingMask)

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showModal, showErrorModal } = useGlobalModalContext()

  // Vars
  const isAllRegionInvisible = useMemo(
    () => maskRegions.every((region) => region.invisible) && savingMask.every((region) => region.invisible),
    [maskRegions, savingMask],
  )

  // Permission check
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)

  /**
   * Handle deletion
   */
  const handleDelete = useCallback(
    (region: MaskRegion) => {
      if (!project || !inspectionArea) {
        return
      }

      showModal({
        title: '注目領域',
        body: <Text>一度削除してしまうと、元に戻せません。</Text>,
        confirmText: '削除',
        modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
        onConfirm: () => {
          void withPromiseContained(async () => {
            dispatch(setSelectedElementIds([region.region_id]))
            dispatch(setElementDeleting(true))

            const token = await getAccessToken()
            if (!token) return

            dispatch(setAttentionText({ message: 'データを更新中...' }))

            const result = await deleteMaskingRegion(
              token,
              project.project_id,
              inspectionArea.inspection_area_id,
              region.region_id,
              showErrorModal,
            )
            if (!result) return

            mixpanel.track('Deleted mask region', {
              'Number of mask region': maskRegions.length - 1,
            })

            dispatch(removeMaskRegion(region.region_id))
          }).finally(() => {
            dispatch(setAttentionText({ message: '' }))
            dispatch(setElementDeleting(false))
            dispatch(setSelectedElementIds([]))
          })
          return true
        },
      })
    },
    [project, inspectionArea, maskRegions.length, getAccessToken, dispatch, showModal, showErrorModal],
  )

  /**
   * Update visibility of all mode value's visibility
   * @param {DepthType} depthType The object should contain the new value of visibility
   */
  const updateAllLayerVisibility = useCallback(
    (visibility: boolean) => {
      dispatch(setMaskRegions(maskRegions.map((region) => ({ ...region, invisible: visibility }))))
    },
    [maskRegions, dispatch],
  )

  /**
   * Update specfic mode value's visibility
   * @param {boolean} visibility New visibility
   * @param {number} index Array index of the mode
   */
  const updateLayerVisibility = useCallback(
    (visibility: boolean, cuboid: MaskRegion) => {
      dispatch(updateMaskRegion({ ...cuboid, invisible: visibility }))
    },
    [dispatch],
  )

  /**
   * Generate the layers for the masking regions panel.
   */
  const layers = useMemo(
    (): EditorPanelItem => ({
      key: 'panel-mask-region-all',
      name: 'すべての点を表示',
      isVirtualContainer: true,
      onSelect: false,
      toggleVisibility: {
        invisible: !isAllRegionInvisible,
        onToggle: () => updateAllLayerVisibility(!isAllRegionInvisible),
      },
      children: [
        // Saved regions
        ...maskRegions.map<EditorPanelItem>((region, index) => ({
          key: `panel-mask-region-${region.region_id}`,
          name: `注目領域 ${index + 1}`,
          item: region,
          onSelect: false,
          toggleVisibility: {
            invisible: region.invisible || false,
            onToggle: () => updateLayerVisibility(!region.invisible!, region),
          },
          onDelete: isAllowedToModify ? () => handleDelete(region) : false,
        })),

        // Saving regions
        ...savingMask.map<EditorPanelItem>((region, index) => ({
          key: `panel-mask-region-saving-${region.region_id}`,
          name: `注目領域 ${maskRegions.length + index + 1}`,
          item: region,
          isWorking: true,
          isSaving: true,
          onSelect: false,
          toggleVisibility: {
            invisible: region.invisible || false,
            onToggle: () => updateLayerVisibility(!region.invisible!, region),
          },
          onDelete: false, // Saving regions cannot be deleted
        })),

        // Working region
        ...(workingMaskPoints
          ? [
              {
                key: `panel-mask-region-working`,
                name: `注目領域 ${maskRegions.length + savingMask.length + 1}`,
                isWorking: true,
                isSaving: false,
                onSelect: false,
                toggleVisibility: null,
                onDelete: () => dispatch(resetWorking()),
              },
            ]
          : []),
      ],
    }),
    [
      maskRegions,
      savingMask,
      workingMaskPoints,
      isAllRegionInvisible,
      isAllowedToModify,
      handleDelete,
      updateAllLayerVisibility,
      updateLayerVisibility,
      dispatch,
    ],
  )

  if (!maskRegions.length && !savingMask.length && !workingMaskPoints) {
    return null
  }

  return <Layer item={layers} depth={-1} forceLayerFullOpacity />
}

export default MaskingRegions
